/**
 * SettingsPage — per-BUID tunable knobs.
 *
 * All values come from the backend registry (`GET /api/admin/config`);
 * this file only renders inputs + persists changes. Adding a new knob
 * on the backend automatically shows up here — no frontend schema to
 * keep in sync.
 *
 * Layout:
 *   ┌── header (title + back button) ───────────────────────────────┐
 *   ├── tabs: Basic  |  Advanced ────────────────────────────────────┤
 *   ├── grouped sections (Shift Windows / Optimization / …)          │
 *   │     each field has label · input · unit · reset · ? tooltip    │
 *   └── sticky action bar: Discard · Reset All · Save Changes ───────┘
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RotateCcw, Save, HelpCircle, AlertTriangle, Check,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { ScrollArea } from "./components/ui/scroll-area";
import { Badge } from "./components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = ({ onBack }) => {
  // BUID header is attached globally by the axios interceptor in App.js,
  // so we don't need a local hook here — just call axios directly.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("basic"); // 'basic' | 'advanced'
  const [knobs, setKnobs] = useState([]); // raw from server
  const [drafts, setDrafts] = useState({}); // key → value (only dirty ones)
  const [buid, setBuid] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/config`);
      setKnobs(res.data.knobs || []);
      setBuid(res.data.buid || "");
      setDrafts({});
    } catch (e) {
      toast.error("Failed to load settings");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Group knobs by their `group` field, honouring the current tab.
  // "advanced" tab shows both basic + advanced so power-users see
  // everything in one place; "basic" hides advanced-only knobs.
  const grouped = useMemo(() => {
    const filtered = knobs.filter(k => tab === "advanced" || k.tab === "basic");
    const out = {};
    for (const k of filtered) {
      const g = k.group || "Other";
      if (!out[g]) out[g] = [];
      out[g].push(k);
    }
    return out;
  }, [knobs, tab]);

  const dirtyCount = Object.keys(drafts).length;
  const hasOverrides = knobs.some(k => k.overridden);

  const setDraft = (key, raw) => {
    setDrafts(d => ({ ...d, [key]: raw }));
  };

  const clearDraft = (key) => {
    setDrafts(d => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const effectiveValue = (k) => {
    if (Object.prototype.hasOwnProperty.call(drafts, k.key)) return drafts[k.key];
    return k.current;
  };

  const resetOneToDefault = async (k) => {
    // If dirty, just clear the draft. Otherwise call server to remove the override.
    if (Object.prototype.hasOwnProperty.call(drafts, k.key)) {
      clearDraft(k.key);
      return;
    }
    if (!k.overridden) return;
    try {
      await axios.post(
        `${API}/api/admin/config/reset`,
        { keys: [k.key] },
      );
      toast.success(`${k.label} reset to default`);
      await load();
    } catch (e) {
      toast.error(`Reset failed for ${k.label}`);
      console.error(e);
    }
  };

  const resetAll = async () => {
    if (!hasOverrides && dirtyCount === 0) {
      toast.message("Nothing to reset");
      return;
    }
    if (!window.confirm(
      "Reset ALL settings for this BUID to code defaults? This cannot be undone.",
    )) return;
    try {
      await axios.post(`${API}/api/admin/config/reset`, {});
      toast.success("All settings reset to defaults");
      await load();
    } catch (e) {
      toast.error("Reset failed");
      console.error(e);
    }
  };

  const save = async () => {
    if (dirtyCount === 0) {
      toast.message("No changes to save");
      return;
    }
    setSaving(true);
    try {
      // Coerce values before sending — inputs give strings; bool knobs
      // come through as JS booleans already.
      const payload = {};
      for (const [k, v] of Object.entries(drafts)) {
        // Look up the knob to determine coercion strategy.
        const knob = knobs.find((x) => x.key === k);
        if (knob && knob.type === "bool") {
          payload[k] = Boolean(v);
        } else {
          payload[k] = v === "" ? null : Number(v);
        }
      }
      const res = await axios.put(
        `${API}/api/admin/config`,
        payload,
      );
      const { applied, errors } = res.data || {};
      if (errors && errors.length) {
        toast.error(
          `${applied} saved, ${errors.length} rejected: ${errors.map(e => `${e.key} (${e.msg})`).join(", ")}`,
        );
      } else {
        toast.success(`${applied} setting${applied === 1 ? "" : "s"} saved`);
      }
      await load();
    } catch (e) {
      toast.error("Save failed");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (dirtyCount === 0) return;
    setDrafts({});
    toast.message("Changes discarded");
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-screen bg-slate-50">
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              data-testid="settings-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-slate-800">Settings</h1>
              <p className="text-xs text-slate-500">
                Tenant: <span className="font-mono font-semibold">{buid || "—"}</span>
                {" · "}
                {knobs.filter(k => k.overridden).length} of {knobs.length} customised
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dirtyCount > 0 && (
              <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {dirtyCount} unsaved
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-white px-6 flex gap-2">
          {[
            { id: "basic", label: "Basic" },
            { id: "advanced", label: "Advanced (all knobs)" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors " +
                (tab === t.id
                  ? "border-slate-800 text-slate-800"
                  : "border-transparent text-slate-500 hover:text-slate-700")
              }
              data-testid={`settings-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto px-6 py-6 space-y-8">
            {loading ? (
              <div className="text-center text-sm text-slate-500 py-12">Loading…</div>
            ) : Object.keys(grouped).length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-12">No knobs to show</div>
            ) : (
              Object.entries(grouped).map(([groupName, groupKnobs]) => (
                <section key={groupName} data-testid={`settings-group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}>
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 border-l-2 border-slate-800 pl-3">
                    {groupName}
                  </h2>
                  <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {groupKnobs.map(k => (
                      <KnobRow
                        key={k.key}
                        knob={k}
                        draftValue={Object.prototype.hasOwnProperty.call(drafts, k.key) ? drafts[k.key] : undefined}
                        effectiveValue={effectiveValue(k)}
                        onChange={(v) => setDraft(k.key, v)}
                        onReset={() => resetOneToDefault(k)}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Sticky action bar.  `pr-40` keeps the Save button clear of the
            Emergent preview badge that lives fixed in the bottom-right corner
            in the preview iframe — otherwise real click hits the badge.
            The badge is not present in production, but the extra padding
            causes no visual harm either. */}
        <div className="border-t border-slate-200 bg-white px-6 pr-40 py-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Changes take effect on the next Cluster Stops / Generate Routes run.
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={discard}
              disabled={dirtyCount === 0}
              data-testid="settings-discard-btn"
            >
              Discard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAll}
              disabled={!hasOverrides && dirtyCount === 0}
              data-testid="settings-reset-all-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Reset all to defaults
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || dirtyCount === 0}
              data-testid="settings-save-btn"
            >
              {saving ? "Saving…" : (
                <>
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

// One row in the settings list.  Kept as its own component so the input
// can be memoised — re-rendering the entire page on every keystroke was
// perceptibly laggy on the Advanced tab (25 inputs).
const KnobRow = ({ knob, draftValue, effectiveValue, onChange, onReset }) => {
  const isDirty = draftValue !== undefined;
  const isOverridden = knob.overridden;

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50/50"
      data-testid={`knob-${knob.key}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-800">{knob.label}</label>
          {knob.help && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-slate-400 hover:text-slate-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-xs">{knob.help}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {isDirty && (
            <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0">
              modified
            </Badge>
          )}
          {!isDirty && isOverridden && (
            <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0">
              <Check className="w-2.5 h-2.5 mr-0.5" />
              customised
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 leading-snug">{knob.description}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {knob.type === "bool" ? (
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(effectiveValue)}
            onClick={() => onChange(!effectiveValue)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              Boolean(effectiveValue) ? "bg-emerald-500" : "bg-slate-300"
            }`}
            data-testid={`knob-toggle-${knob.key}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                Boolean(effectiveValue) ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        ) : (
          <Input
            type="number"
            value={effectiveValue ?? ""}
            onChange={(e) => onChange(e.target.value)}
            min={knob.min}
            max={knob.max}
            step={knob.step}
            className="w-28 h-9 text-sm text-right"
            data-testid={`knob-input-${knob.key}`}
          />
        )}
        <span className="text-xs text-slate-500 w-10">{knob.unit}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={!isDirty && !isOverridden}
          title={`Reset to default (${knob.default}${knob.unit})`}
          className="h-9 w-9 p-0"
          data-testid={`knob-reset-${knob.key}`}
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
